import { useEffect, useMemo } from 'react';
import { areaTagSetKeys, projectLayoutsQueryOptions, projectLocationQueryOptions } from '@/features/area-tag-sets/hooks';
import { photoGalleryKeys } from '@/features/photo-gallery/hooks';
import { useInfiniteQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { walkthroughKeys } from '@ironview-360/api-client/walkthroughs';
import {
  getPunchListItemsPage,
  getPunchListItemById,
  getPunchListFilterViews,
  createPunchListFilterView,
  deletePunchListFilterView,
  createPunchListItem,
  updatePunchListItem,
  deletePunchListItem,
  approvePunchListItem,
  rejectPunchListItem,
  sendPunchListReminders,
  getPunchListStatusSummary,
  getPunchListItemTypes,
  createPunchListItemType,
  getTrades,
  createTrade,
  getTradePartners,
  getCostCodes,
  createCostCode,
  getProjectShareableUsers,
  createAreaTagForProject,
  uploadPunchListAttachment,
  linkPunchListAttachmentPhoto,
  deletePunchListAttachment,
  shareUnitPunchListReports,
  archivePunchItemWorkflow,
  addPunchWorkflowBatchRun,
  createPunchWorkflowBatch,
  createPunchWorkflowRun,
  createPunchItemWorkflow,
  getPunchWorkflowBatches,
  getPunchWorkflowRun,
  getPunchWorkflowRuns,
  getPunchItemWorkflowTemplates,
  updatePunchItemWorkflow,
  getPunchListExportPreferences,
  updatePunchListExportPreferences,
  getPunchWalkthrough,
  getPunchWalkthroughs,
  publishPunchWalkthrough,
  reviewPunchWalkthroughItem,
  uploadPunchWalkthroughPhoto,
  uploadAndFinalizePunchWalkthrough,
  cancelPunchWalkthrough,
  savePunchWalkthroughDrafts,
  retryPunchWalkthrough,
  finalizePunchWalkthrough,
  createQuickPunchItem,
  getQuickPunchConfiguration,
  getQuickPunchProjectConfiguration,
  updateQuickPunchPreference,
  updateQuickPunchProjectConfiguration,
} from '../api/api';
import type {
  CreatePunchListFilterViewData,
  CreatePunchListItemData,
  UpdatePunchListItemData,
  PunchListItemFilters,
  SendPunchListRemindersData,
  ShareUnitPunchListReportsData,
  CreatePunchWorkflowBatchData,
  AddPunchWorkflowBatchRunData,
  CreatePunchWorkflowRunData,
  CreatePunchItemWorkflowData,
  PunchWorkflowBatch,
  PunchWorkflowRun,
  PunchItemWorkflow,
  PunchListAttachmentKind,
  PunchListExportPreferences,
  PunchListCsvColumnKey,
  PunchWalkthroughDraftUpdate,
  PunchWalkthroughSession,
  PunchWalkthroughUploadResponse,
  QuickPunchConfigurationResponse,
  QuickPunchDefaultValue,
  QuickPunchFieldKey,
} from '../types';

// ─── Query key factory ────────────────────────────────────────────────────────

export const punchListKeys = {
  all: ['punch-lists'] as const,
  lists: (projectId: number) => [...punchListKeys.all, 'list', projectId] as const,
  list: (projectId: number, filters: PunchListItemFilters) =>
    [...punchListKeys.lists(projectId), filters] as const,
  pages: (projectId: number, filters: PunchListItemFilters) =>
    [...punchListKeys.lists(projectId), 'pages', filters] as const,
  details: () => [...punchListKeys.all, 'detail'] as const,
  detail: (id: string) => [...punchListKeys.details(), id] as const,
  statusSummary: (projectId: number) =>
    [...punchListKeys.all, 'status-summary', projectId] as const,
  filterViews: (projectId: number) => [...punchListKeys.all, 'filter-views', projectId] as const,
  exportPreferences: () => [...punchListKeys.all, 'export-preferences'] as const,
  quickPunchConfiguration: (projectId: number) =>
    [...punchListKeys.all, 'quick-punch-configuration', projectId] as const,
  quickPunchProjectConfiguration: (projectId: number) =>
    [...punchListKeys.all, 'quick-punch-project-configuration', projectId] as const,
  itemTypes: () => [...punchListKeys.all, 'item-types'] as const,
  trades: () => [...punchListKeys.all, 'trades'] as const,
  tradePartners: (projectId: number) =>
    [...punchListKeys.all, 'trade-partners', projectId] as const,
  costCodes: () => [...punchListKeys.all, 'cost-codes'] as const,
  workflowTemplates: (projectId: number) =>
    [...punchListKeys.all, 'workflow-templates', projectId] as const,
  workflowRuns: (projectId: number) => [...punchListKeys.all, 'workflow-runs', projectId] as const,
  workflowBatches: (projectId: number) =>
    [...punchListKeys.all, 'workflow-batches', projectId] as const,
  workflowRun: (projectId: number, runId: string) =>
    [...punchListKeys.workflowRuns(projectId), 'detail', runId] as const,
  walkthrough: walkthroughKeys.detail,
  walkthroughs: walkthroughKeys.lists,
};

// ─── Read hooks ───────────────────────────────────────────────────────────────

export const usePunchListItems = (
  projectId: number | undefined,
  filters: PunchListItemFilters = {},
  { progressive = false }: { progressive?: boolean } = {},
) => {
  const query = useInfiniteQuery({
    queryKey: punchListKeys.pages(projectId!, filters),
    queryFn: ({ pageParam, signal }) => getPunchListItemsPage(projectId!, filters, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next ?? undefined,
    enabled: !!projectId,
    staleTime: 30_000,
  });
  const { hasNextPage, isFetching, isError, fetchNextPage } = query;
  // Continue loading for client-side filters, sorting, summaries and bulk actions.
  // Multiple observers share this query; never cancel an in-flight page fetch.
  useEffect(() => {
    if (projectId && hasNextPage && !isFetching && !isError) {
      void fetchNextPage({ cancelRefetch: false });
    }
  }, [projectId, hasNextPage, isFetching, isError, fetchNextPage]);

  const items = useMemo(() => query.data?.pages.flatMap((page) => page.results), [query.data]);
  const isComplete = !!query.data && !hasNextPage && !isError;
  return {
    ...query,
    // Existing consumers only receive complete arrays; the table opts into previews.
    data: progressive || isComplete ? items : undefined,
    isLoading: progressive ? query.isLoading : !isComplete && !isError && !!projectId,
    isComplete,
    totalCount: query.data?.pages[0]?.count ?? 0,
  };
};

export const usePunchListItem = (id: string | undefined, projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.detail(id!),
    queryFn: () => getPunchListItemById(id!, projectId!),
    enabled: !!id && !!projectId,
    staleTime: 60_000,
  });

export const usePunchListStatusSummary = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.statusSummary(projectId!),
    queryFn: () => getPunchListStatusSummary(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const usePunchWalkthrough = (
  projectId: number | undefined,
  sessionId: string | undefined,
) => useQuery({
  queryKey: punchListKeys.walkthrough(projectId!, sessionId!),
  queryFn: ({ signal }) => getPunchWalkthrough(projectId!, sessionId!, signal),
  enabled: !!projectId && !!sessionId,
  staleTime: 60_000,
  refetchOnWindowFocus: false,
  refetchInterval: (query) => {
    const status = query.state.data?.status;
    return status === 'UPLOADED' || status === 'PROCESSING' ? 5_000 : false;
  },
});

export const usePunchWalkthroughs = (projectId: number | undefined, page = 1, scope: 'active' | 'history' = 'active') => useQuery({
  queryKey: walkthroughKeys.list(projectId!, page, scope),
  queryFn: ({ signal }) => getPunchWalkthroughs(projectId!, page, scope, signal),
  enabled: !!projectId,
  staleTime: 30_000,
  refetchInterval: (query) => query.state.data?.items.some(
    (session) => session.status === 'UPLOADED' || session.status === 'PROCESSING',
  ) ? 10_000 : false,
});

export const usePunchWalkthroughMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();

  const upload = useMutation({
    mutationFn: ({
      file,
      onProgress,
      onCreated,
      resume,
    }: {
      file: File;
      onProgress?: (progressPct: number) => void;
      onCreated?: (upload: PunchWalkthroughUploadResponse) => void;
      resume?: PunchWalkthroughUploadResponse;
    }) => uploadAndFinalizePunchWalkthrough(projectId!, file, onProgress, onCreated, resume),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: punchListKeys.walkthroughs(projectId!) });
      return {};
    },
    onSuccess: (session) => {
      queryClient.setQueryData(
        punchListKeys.walkthrough(projectId!, session.id),
        session,
      );
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthroughs(projectId!) });
    },
    onError: () => undefined,
    onSettled: (session) => {
      if (session) {
        queryClient.invalidateQueries({
          queryKey: punchListKeys.walkthrough(projectId!, session.id),
        });
      }
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthroughs(projectId!) });
    },
  });

  const publish = useMutation({
    mutationFn: ({
      sessionId,
      drafts,
      revision,
    }: {
      sessionId: string;
      drafts: PunchWalkthroughDraftUpdate[];
      revision?: number;
    }) => publishPunchWalkthrough(projectId!, sessionId, drafts, revision),
    onMutate: async ({ sessionId, drafts }) => {
      const queryKey = punchListKeys.walkthrough(projectId!, sessionId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PunchWalkthroughSession>(queryKey);
      if (previous) {
        // Only edits are optimistic. PUBLISHED is authoritative server state.
        queryClient.setQueryData(queryKey, { ...previous, drafts: previous.drafts.map(
          (draft) => ({ ...draft, ...drafts.find((update) => update.id === draft.id) }),
        ) });
      }
      return { previous, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSuccess: (result) => {
      queryClient.setQueryData(
        punchListKeys.walkthrough(projectId!, result.session.id),
        result.session,
      );
    },
    onSettled: (result, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: punchListKeys.walkthrough(projectId!, result?.session.id ?? variables.sessionId),
      });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: photoGalleryKeys.lists(projectId) });
        queryClient.invalidateQueries({ queryKey: punchListKeys.walkthroughs(projectId) });
        queryClient.invalidateQueries({ queryKey: punchListKeys.lists(projectId) });
        queryClient.invalidateQueries({ queryKey: punchListKeys.statusSummary(projectId) });
      }
    },
  });

  const cancel = useMutation({
    mutationFn: (sessionId: string) => cancelPunchWalkthrough(projectId!, sessionId),
    onMutate: async (sessionId) => {
      const queryKey = punchListKeys.walkthrough(projectId!, sessionId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PunchWalkthroughSession>(queryKey);
      if (previous) {
        queryClient.setQueryData(queryKey, { ...previous, status: 'CANCELLED' });
      }
      return { previous, queryKey };
    },
    onError: (_error, _sessionId, context) => {
      if (context?.previous) queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSettled: (result, _error, sessionId) => {
      queryClient.invalidateQueries({
        queryKey: punchListKeys.walkthrough(projectId!, result?.id ?? sessionId),
      });
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthroughs(projectId!) });
    },
  });

  const review = useMutation({
    mutationFn: ({ sessionId, input }: {
      sessionId: string;
      input: Parameters<typeof reviewPunchWalkthroughItem>[2];
    }) => reviewPunchWalkthroughItem(projectId!, sessionId, input),
    onMutate: async ({ sessionId, input }) => {
      const queryKey = punchListKeys.walkthrough(projectId!, sessionId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PunchWalkthroughSession>(queryKey);
      // Only edits are optimistic. Never advance the review before confirmation.
      if (previous && input.decision === 'publish') queryClient.setQueryData(queryKey, {
        ...previous, drafts: previous.drafts.map((draft) => draft.id === input.draft_id
          ? { ...draft, ...input.draft } : draft),
      });
      return { queryKey, previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSuccess: (result) => queryClient.setQueryData(punchListKeys.walkthrough(projectId!, result.session.id), result.session),
    onSettled: (_result, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthrough(projectId!, variables.sessionId) });
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthroughs(projectId!) });
      queryClient.invalidateQueries({ queryKey: punchListKeys.lists(projectId!) });
      queryClient.invalidateQueries({ queryKey: punchListKeys.statusSummary(projectId!) });
      queryClient.invalidateQueries({ queryKey: photoGalleryKeys.lists(projectId) });
    },
  });

  const save = useMutation({
    mutationFn: ({ sessionId, drafts, revision }: { sessionId: string; drafts: PunchWalkthroughDraftUpdate[]; revision: number }) =>
      savePunchWalkthroughDrafts(projectId!, sessionId, drafts, revision),
    onMutate: async ({ sessionId, drafts }) => {
      const queryKey = punchListKeys.walkthrough(projectId!, sessionId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PunchWalkthroughSession>(queryKey);
      if (previous) queryClient.setQueryData(queryKey, { ...previous, drafts: previous.drafts.map(
        (draft) => ({ ...draft, ...drafts.find((update) => update.id === draft.id) }),
      ) });
      return { previous, queryKey };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSuccess: (session) => queryClient.setQueryData(punchListKeys.walkthrough(projectId!, session.id), session),
    onSettled: (_result, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthrough(projectId!, variables.sessionId) });
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthroughs(projectId!) });
    },
  });
  const retry = useMutation({
    mutationFn: ({ sessionId, finalize }: { sessionId: string; finalize?: boolean }) => finalize
      ? finalizePunchWalkthrough(projectId!, sessionId) : retryPunchWalkthrough(projectId!, sessionId),
    onMutate: async ({ sessionId }) => {
      const queryKey = punchListKeys.walkthrough(projectId!, sessionId);
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PunchWalkthroughSession>(queryKey);
      if (previous) queryClient.setQueryData(queryKey, { ...previous, status: 'UPLOADED', processing_stage: 'queued' });
      return { queryKey, previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSuccess: (session) => queryClient.setQueryData(punchListKeys.walkthrough(projectId!, session.id), session),
    onSettled: (_result, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthrough(projectId!, variables.sessionId) });
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthroughs(projectId!) });
    },
  });

  const uploadPhoto = useMutation({
    mutationFn: ({ sessionId, draftId, revision, file }: {
      sessionId: string; draftId: string; revision: number; file: File;
    }) => uploadPunchWalkthroughPhoto(projectId!, sessionId, draftId, revision, file),
    onMutate: async ({ sessionId }) => {
      const queryKey = punchListKeys.walkthrough(projectId!, sessionId);
      await queryClient.cancelQueries({ queryKey });
      // The server assigns the photo ID; local upload progress is shown until it exists.
      return { queryKey, previous: queryClient.getQueryData<PunchWalkthroughSession>(queryKey) };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(context.queryKey, context.previous);
    },
    onSuccess: (session) => queryClient.setQueryData(punchListKeys.walkthrough(projectId!, session.id), session),
    onSettled: (_result, _error, variables) => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthrough(projectId!, variables.sessionId) });
      queryClient.invalidateQueries({ queryKey: punchListKeys.walkthroughs(projectId!) });
    },
  });

  return { upload, publish, review, cancel, save, retry, uploadPhoto };
};

export const usePunchListFilterViews = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.filterViews(projectId!),
    queryFn: () => getPunchListFilterViews(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const usePunchListExportPreferences = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.exportPreferences(),
    queryFn: () => getPunchListExportPreferences(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useUpdatePunchListExportPreferences = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  const queryKey = punchListKeys.exportPreferences();

  return useMutation({
    mutationFn: (selectedColumns: PunchListCsvColumnKey[]) =>
      updatePunchListExportPreferences(projectId!, selectedColumns),
    onMutate: async (selectedColumns) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PunchListExportPreferences>(queryKey);
      if (previous) {
        queryClient.setQueryData<PunchListExportPreferences>(queryKey, {
          ...previous,
          selected_columns: selectedColumns,
        });
      }
      return { previous };
    },
    onError: (_error, _selectedColumns, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
};

export const useQuickPunchConfiguration = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.quickPunchConfiguration(projectId!),
    queryFn: () => getQuickPunchConfiguration(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useQuickPunchProjectConfiguration = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.quickPunchProjectConfiguration(projectId!),
    queryFn: () => getQuickPunchProjectConfiguration(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

type QuickPunchPreferenceInput = {
  visible_fields: QuickPunchFieldKey[];
  required_fields: QuickPunchFieldKey[];
  defaults: Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;
};

export const useUpdateQuickPunchPreference = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  const queryKey = punchListKeys.quickPunchConfiguration(projectId!);
  return useMutation({
    mutationFn: (payload: QuickPunchPreferenceInput) =>
      updateQuickPunchPreference(projectId!, payload),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      return { previous: queryClient.getQueryData<QuickPunchConfigurationResponse>(queryKey) };
    },
    onError: (_error, _payload, context) => {
      if (context?.previous) queryClient.setQueryData(queryKey, context.previous);
    },
    onSuccess: (data) => queryClient.setQueryData(queryKey, data),
    onSettled: () => queryClient.invalidateQueries({ queryKey }),
  });
};

type QuickPunchProjectConfigurationInput = {
  required_fields: QuickPunchFieldKey[];
  defaults: Partial<Record<QuickPunchFieldKey, QuickPunchDefaultValue>>;
};

export const useUpdateQuickPunchProjectConfiguration = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  const projectKey = punchListKeys.quickPunchProjectConfiguration(projectId!);
  const userKey = punchListKeys.quickPunchConfiguration(projectId!);
  return useMutation({
    mutationFn: (payload: QuickPunchProjectConfigurationInput) =>
      updateQuickPunchProjectConfiguration(projectId!, payload),
    onMutate: async () => {
      await Promise.all([
        queryClient.cancelQueries({ queryKey: projectKey }),
        queryClient.cancelQueries({ queryKey: userKey }),
      ]);
      return {
        previousProject: queryClient.getQueryData<QuickPunchConfigurationResponse>(projectKey),
        previousUser: queryClient.getQueryData<QuickPunchConfigurationResponse>(userKey),
      };
    },
    onError: (_error, _payload, context) => {
      if (context?.previousProject) queryClient.setQueryData(projectKey, context.previousProject);
      if (context?.previousUser) queryClient.setQueryData(userKey, context.previousUser);
    },
    onSuccess: (data) => queryClient.setQueryData(projectKey, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: projectKey });
      queryClient.invalidateQueries({ queryKey: userKey });
    },
  });
};

export const useCreatePunchListFilterView = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreatePunchListFilterViewData) => createPunchListFilterView(payload),
    onSuccess: () => {
      if (!projectId) return;
      queryClient.invalidateQueries({ queryKey: punchListKeys.filterViews(projectId) });
    },
  });
};

export const useDeletePunchListFilterView = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deletePunchListFilterView(id),
    onSuccess: () => {
      if (!projectId) return;
      queryClient.invalidateQueries({ queryKey: punchListKeys.filterViews(projectId) });
    },
  });
};

export const usePunchListItemTypes = () =>
  useQuery({
    queryKey: punchListKeys.itemTypes(),
    queryFn: getPunchListItemTypes,
    staleTime: 5 * 60_000,
  });

export const useCreatePunchListItemType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createPunchListItemType(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.itemTypes() });
    },
  });
};

export const useTrades = () =>
  useQuery({
    queryKey: punchListKeys.trades(),
    queryFn: getTrades,
    staleTime: 5 * 60_000,
  });

export const useCreateTrade = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => createTrade(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.trades() });
    },
  });
};

export const useTradePartners = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.tradePartners(projectId!),
    queryFn: () => getTradePartners(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useCostCodes = () =>
  useQuery({
    queryKey: punchListKeys.costCodes(),
    queryFn: getCostCodes,
    staleTime: 5 * 60_000,
  });

export const useCreateCostCode = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; code: string }) => createCostCode(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.costCodes() });
    },
  });
};

export const useProjectUsers = (projectId: number | undefined) =>
  useQuery({
    queryKey: [...punchListKeys.all, 'project-users', projectId] as const,
    queryFn: () => getProjectShareableUsers(projectId!),
    enabled: !!projectId,
    staleTime: 5 * 60_000,
  });

export const useProjectAreaTags = (projectId: number | undefined) => {
  const client = useQueryClient();
  return useQuery({
    ...projectLocationQueryOptions(projectId!, client),
    select: (locations) => locations.areaTags,
    enabled: !!projectId,
  });
};

export const useProjectLayouts = (projectId: number | undefined) =>
  useQuery({
    ...projectLayoutsQueryOptions(projectId!),
    enabled: !!projectId,
  });

export const useCreateAreaTag = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { name: string; layout_id: number }) =>
      createAreaTagForProject({ ...payload, project_id: projectId! }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: areaTagSetKeys.projectLocationOptions(projectId!),
      });
    },
  });
};

export const usePunchItemWorkflowTemplates = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.workflowTemplates(projectId!),
    queryFn: () => getPunchItemWorkflowTemplates(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const usePunchWorkflowRuns = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.workflowRuns(projectId!),
    queryFn: () => getPunchWorkflowRuns(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const usePunchWorkflowBatches = (projectId: number | undefined) =>
  useQuery({
    queryKey: punchListKeys.workflowBatches(projectId!),
    queryFn: () => getPunchWorkflowBatches(projectId!),
    enabled: !!projectId,
    staleTime: 30_000,
  });

export const usePunchWorkflowRun = (projectId: number | undefined, runId: string | undefined) =>
  useQuery({
    queryKey: punchListKeys.workflowRun(projectId!, runId!),
    queryFn: () => getPunchWorkflowRun(projectId!, runId!),
    enabled: !!projectId && !!runId,
    staleTime: 30_000,
  });

export const usePunchWorkflowMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();
  const templatesKey = punchListKeys.workflowTemplates(projectId!);
  const runsKey = punchListKeys.workflowRuns(projectId!);
  const batchesKey = punchListKeys.workflowBatches(projectId!);

  const createWorkflow = useMutation({
    mutationFn: (data: CreatePunchItemWorkflowData) => createPunchItemWorkflow(data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: templatesKey });
      const previous = queryClient.getQueryData<PunchItemWorkflow[]>(templatesKey) ?? [];
      const now = new Date().toISOString();
      const optimistic: PunchItemWorkflow = {
        id: `optimistic-${Date.now()}`,
        project: data.project,
        client: 0,
        name: data.name,
        description: data.description ?? '',
        is_template: data.is_template,
        steps: data.steps.map((step, index) => ({
          id: `optimistic-step-${index}`,
          stage: step.stage,
          position: step.position,
          title: step.title,
          description: step.description ?? '',
          trade: step.trade ?? null,
          trade_name: null,
          responsible_trade_partner: step.responsible_trade_partner ?? null,
          responsible_trade_partner_name: null,
          priority: step.priority ?? 'MEDIUM',
          due_days_after_activation: step.due_days_after_activation ?? 0,
        })),
        stage_count: new Set(data.steps.map((step) => step.stage)).size,
        created_by: null,
        is_archived: false,
        created_at: now,
        updated_at: now,
      };
      queryClient.setQueryData(templatesKey, [...previous, optimistic]);
      return { previous };
    },
    onError: (_error, _data, context) => {
      queryClient.setQueryData(templatesKey, context?.previous ?? []);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: templatesKey }),
  });

  const updateWorkflow = useMutation({
    mutationFn: ({ id, data }: { id: string; data: CreatePunchItemWorkflowData }) =>
      updatePunchItemWorkflow(id, data.project, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: templatesKey });
      const previous = queryClient.getQueryData<PunchItemWorkflow[]>(templatesKey) ?? [];
      queryClient.setQueryData<PunchItemWorkflow[]>(
        templatesKey,
        previous.map((template) =>
          template.id === id
            ? {
                ...template,
                name: data.name,
                description: data.description ?? '',
                is_template: data.is_template,
                steps: data.steps.map((step, index) => ({
                  id: template.steps[index]?.id ?? `optimistic-step-${index}`,
                  stage: step.stage,
                  position: step.position,
                  title: step.title,
                  description: step.description ?? '',
                  trade: step.trade ?? null,
                  trade_name: null,
                  responsible_trade_partner: step.responsible_trade_partner ?? null,
                  responsible_trade_partner_name: null,
                  priority: step.priority ?? 'MEDIUM',
                  due_days_after_activation: step.due_days_after_activation ?? 0,
                })),
                stage_count: new Set(data.steps.map((step) => step.stage)).size,
              }
            : template
        )
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(templatesKey, context?.previous ?? []);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: templatesKey }),
  });

  const archiveWorkflow = useMutation({
    mutationFn: (id: string) => archivePunchItemWorkflow(id, projectId!),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: templatesKey });
      const previous = queryClient.getQueryData<PunchItemWorkflow[]>(templatesKey) ?? [];
      queryClient.setQueryData(
        templatesKey,
        previous.filter((template) => template.id !== id)
      );
      return { previous };
    },
    onError: (_error, _id, context) => {
      queryClient.setQueryData(templatesKey, context?.previous ?? []);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: templatesKey }),
  });

  const launchRun = useMutation({
    mutationFn: (data: CreatePunchWorkflowRunData) => createPunchWorkflowRun(projectId!, data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: runsKey });
      const previous = queryClient.getQueryData<PunchWorkflowRun[]>(runsKey) ?? [];
      const workflow = queryClient
        .getQueryData<PunchItemWorkflow[]>(templatesKey)
        ?.find((candidate) => candidate.id === data.workflow);
      const definitionSteps = workflow?.steps ?? data.steps ?? [];
      const now = new Date().toISOString();
      const optimistic: PunchWorkflowRun = {
        id: `optimistic-${Date.now()}`,
        workflow: data.workflow ?? null,
        batch: null,
        workflow_name: workflow?.name ?? data.name ?? 'Starting workflow…',
        project: projectId!,
        status: 'ACTIVE',
        current_stage: 1,
        total_stages: new Set(definitionSteps.map((step) => step.stage)).size || 1,
        total_steps: definitionSteps.length,
        completed_steps: 0,
        definition: definitionSteps.map((step, index) => ({
          step_key:
            'id' in step && typeof step.id === 'string' ? step.id : `optimistic-step-${index}`,
          stage: step.stage,
          position: step.position,
          title: step.title,
          description: step.description ?? '',
          trade: step.trade ?? null,
          responsible_trade_partner: step.responsible_trade_partner ?? null,
          priority: step.priority ?? 'MEDIUM',
          due_days_after_activation: step.due_days_after_activation ?? 0,
        })),
        location: null,
        hotspot: null,
        items: [],
        created_by: null,
        completed_at: null,
        created_at: now,
        updated_at: now,
      };
      queryClient.setQueryData(runsKey, [optimistic, ...previous]);
      return { previous };
    },
    onError: (_error, _data, context) => {
      queryClient.setQueryData(runsKey, context?.previous ?? []);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: runsKey });
      queryClient.invalidateQueries({ queryKey: templatesKey });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: punchListKeys.lists(projectId) });
        queryClient.invalidateQueries({ queryKey: punchListKeys.statusSummary(projectId) });
      }
    },
  });

  const launchBatch = useMutation({
    mutationFn: (data: CreatePunchWorkflowBatchData) => createPunchWorkflowBatch(projectId!, data),
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey: batchesKey });
      const previous = queryClient.getQueryData<PunchWorkflowBatch[]>(batchesKey) ?? [];
      const workflow = queryClient
        .getQueryData<PunchItemWorkflow[]>(templatesKey)
        ?.find((candidate) => candidate.id === data.workflow);
      const definitionSteps = workflow?.steps ?? data.steps ?? [];
      const now = new Date().toISOString();
      const optimistic: PunchWorkflowBatch = {
        id: `optimistic-${Date.now()}`,
        workflow: data.workflow ?? null,
        name: data.name || workflow?.name || data.workflow_name || 'Starting workflow…',
        project: projectId!,
        status: 'ACTIVE',
        current_stage: 1,
        total_stages: new Set(definitionSteps.map((step) => step.stage)).size || 1,
        total_runs: data.locations?.length ?? 1,
        total_steps: definitionSteps.length * (data.locations?.length ?? 1),
        completed_steps: 0,
        runs: [],
        created_by: null,
        completed_at: null,
        created_at: now,
        updated_at: now,
      };
      queryClient.setQueryData(batchesKey, [optimistic, ...previous]);
      return { previous };
    },
    onError: (_error, _data, context) => {
      queryClient.setQueryData(batchesKey, context?.previous ?? []);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: batchesKey });
      queryClient.invalidateQueries({ queryKey: runsKey });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: punchListKeys.lists(projectId) });
        queryClient.invalidateQueries({ queryKey: punchListKeys.statusSummary(projectId) });
      }
    },
  });

  const addRunToBatch = useMutation({
    mutationFn: ({ batchId, data }: { batchId: string; data: AddPunchWorkflowBatchRunData }) =>
      addPunchWorkflowBatchRun(projectId!, batchId, data),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: batchesKey });
      queryClient.invalidateQueries({ queryKey: runsKey });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: punchListKeys.lists(projectId) });
        queryClient.invalidateQueries({ queryKey: punchListKeys.statusSummary(projectId) });
      }
    },
  });

  return {
    createWorkflow,
    updateWorkflow,
    archiveWorkflow,
    launchRun,
    launchBatch,
    addRunToBatch,
  };
};

// ─── Mutation hooks ───────────────────────────────────────────────────────────

export const usePunchListMutations = (projectId: number | undefined) => {
  const queryClient = useQueryClient();

  const invalidateLists = () => {
    if (!projectId) return;
    queryClient.invalidateQueries({ queryKey: punchListKeys.lists(projectId) });
    queryClient.invalidateQueries({ queryKey: punchListKeys.statusSummary(projectId) });
    queryClient.invalidateQueries({ queryKey: punchListKeys.workflowRuns(projectId) });
    queryClient.invalidateQueries({ queryKey: punchListKeys.workflowBatches(projectId) });
  };

  const createItem = useMutation({
    mutationFn: (data: CreatePunchListItemData) => createPunchListItem(data),
    onSuccess: () => invalidateLists(),
  });

  const createQuickItem = useMutation({
    mutationFn: (
      data: Partial<CreatePunchListItemData> &
        Pick<CreatePunchListItemData, 'project' | 'description'>
    ) => createQuickPunchItem(data),
    onMutate: async () => {
      if (!projectId) return { previousLists: [] as Array<[readonly unknown[], unknown]> };
      const prefix = punchListKeys.lists(projectId);
      await queryClient.cancelQueries({ queryKey: prefix });
      return { previousLists: queryClient.getQueriesData({ queryKey: prefix }) };
    },
    onError: (_error, _data, context) => {
      context?.previousLists.forEach(([queryKey, value]) => {
        queryClient.setQueryData(queryKey, value);
      });
    },
    onSettled: () => invalidateLists(),
  });

  const updateItem = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdatePunchListItemData }) =>
      updatePunchListItem(id, data),
    onSuccess: (_result, { id }) => {
      // The update endpoint may return its write-serializer shape, which omits
      // detail-only fields such as attachments. Keep the complete cached item
      // mounted and refetch the canonical detail instead of replacing it with
      // a partial response.
      queryClient.invalidateQueries({ queryKey: punchListKeys.detail(id) });
      invalidateLists();
    },
  });

  const deleteItem = useMutation({
    mutationFn: (id: string) => deletePunchListItem(id),
    onSuccess: () => invalidateLists(),
  });

  const approveItem = useMutation({
    mutationFn: (id: string) => approvePunchListItem(id),
    onSuccess: (result) => {
      queryClient.setQueryData(punchListKeys.detail(result.id), result);
      invalidateLists();
    },
  });

  const rejectItem = useMutation({
    mutationFn: (id: string) => rejectPunchListItem(id),
    onSuccess: (result) => {
      queryClient.setQueryData(punchListKeys.detail(result.id), result);
      invalidateLists();
    },
  });

  const sendReminders = useMutation({
    mutationFn: (data: SendPunchListRemindersData) => sendPunchListReminders(data),
  });

  const shareUnitReports = useMutation({
    mutationFn: (data: ShareUnitPunchListReportsData) => shareUnitPunchListReports(data),
  });

  const uploadAttachment = useMutation({
    mutationFn: ({
      itemId,
      file,
      kind = 'general',
    }: {
      itemId: string;
      file: File;
      kind?: PunchListAttachmentKind;
    }) => uploadPunchListAttachment(itemId, file, kind),
    onSuccess: (_data, { itemId }) => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.detail(itemId) });
      if (projectId) {
        queryClient.invalidateQueries({ queryKey: punchListKeys.workflowRuns(projectId) });
        queryClient.invalidateQueries({ queryKey: punchListKeys.workflowBatches(projectId) });
      }
    },
  });

  const linkAttachmentPhoto = useMutation({
    mutationFn: ({
      itemId,
      photoId,
      kind = 'general',
    }: {
      itemId: string;
      photoId: number;
      kind?: PunchListAttachmentKind;
    }) => linkPunchListAttachmentPhoto(itemId, photoId, kind),
    onSuccess: (_data, { itemId }) => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.detail(itemId) });
    },
  });

  const deleteAttachment = useMutation({
    mutationFn: ({ attachmentId, itemId }: { attachmentId: string; itemId: string }) =>
      deletePunchListAttachment(attachmentId).then(() => itemId),
    onSuccess: (itemId) => {
      queryClient.invalidateQueries({ queryKey: punchListKeys.detail(itemId) });
    },
  });

  return {
    createItem,
    createQuickItem,
    updateItem,
    deleteItem,
    approveItem,
    rejectItem,
    sendReminders,
    shareUnitReports,
    uploadAttachment,
    linkAttachmentPhoto,
    deleteAttachment,
  };
};
